import React, { useState, useEffect, useCallback } from 'react';
import {
  FileText, Calendar, Send, Download, ChevronDown, ChevronUp,
  Eye, Package, MessageSquare, MapPin, Globe, TrendingUp, TrendingDown,
  Clock, CheckCircle, AlertCircle, Loader, ExternalLink, BarChart3
} from 'lucide-react';

const API = process.env.REACT_APP_BACKEND_URL;
const WEB3FORMS_KEY = '4deb5ebc-3d5b-446d-9890-9acae8b33da4';
const PORTAL_URL = 'https://totalcomposites.com/portal';

const WeeklyReportsSection = ({ authHeaders }) => {
  const [reports, setReports] = useState([]);
  const [selectedReport, setSelectedReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [sending, setSending] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showSelector, setShowSelector] = useState(false);

  const showFlash = (msg, isError = false) => {
    setFlash({ msg, isError });
    setTimeout(() => setFlash(null), 4000);
  };

  const fetchReports = useCallback(async () => {
    try {
      const res = await fetch(`${API}/api/portal/admin/reports`, { headers: authHeaders });
      if (res.ok) {
        const data = await res.json();
        setReports(data);
        if (data.length > 0 && !selectedReport) {
          setSelectedReport(data[0]);
        }
      }
    } catch (e) { console.error(e); }
    setLoading(false);
  }, [authHeaders, selectedReport]);

  useEffect(() => { fetchReports(); }, [fetchReports]);

  const handleGenerate = async () => {
    setGenerating(true);
    try {
      const res = await fetch(`${API}/api/portal/admin/reports/generate`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
      });
      if (res.ok) {
        const report = await res.json();
        setReports(prev => [report, ...prev]);
        setSelectedReport(report);
        showFlash('Report generated successfully');
      } else {
        showFlash('Failed to generate report', true);
      }
    } catch (e) {
      showFlash('Error generating report', true);
    }
    setGenerating(false);
  };

  const handleSendDigest = async (reportId) => {
    setSending(true);
    try {
      // First get the full report data
      const res = await fetch(`${API}/api/portal/admin/reports/send-digest`, {
        method: 'POST',
        headers: { ...authHeaders, 'Content-Type': 'application/json' },
        body: JSON.stringify({ report_id: reportId }),
      });
      const report = await res.json();
      if (!res.ok) {
        showFlash(report.detail || 'Failed to get report', true);
        setSending(false);
        return;
      }

      const stats = report.stats || {};
      const changes = report.changes || {};
      const period = report.period || {};
      const arrow = (v) => v >= 0 ? `+${v}%` : `${v}%`;

      const countriesText = (report.top_countries || []).slice(0, 5)
        .map(c => `  ${c.country || 'Unknown'}: ${c.count} visits`).join('\n');
      const pagesText = (report.top_pages || []).slice(0, 5)
        .map(p => `  ${p.page}: ${p.count} views`).join('\n');

      const message = `TOTAL COMPOSITES - Weekly Analytics Digest
${'='.repeat(50)}
Period: ${period.label || 'N/A'}
Report generated: ${(report.generated_at || '').slice(0, 16)}

--- Performance Summary ---
  Visits / Sessions: ${stats.visits || 0}  (${arrow(changes.visits || 0)} vs last week)
  Orders: ${stats.orders || 0}  (${arrow(changes.orders || 0)} vs last week)
  Chat Sessions: ${stats.chats || 0}  (${arrow(changes.chats || 0)} vs last week)
  Contact Forms: ${stats.forms || 0}  (${arrow(changes.forms || 0)} vs last week)
  Total Leads: ${stats.total_leads || 0}
${countriesText ? `\n--- Top Countries ---\n${countriesText}` : ''}
${pagesText ? `\n--- Top Pages ---\n${pagesText}` : ''}

--- View Full Report ---
${PORTAL_URL}

This is an automated weekly digest from TOTAL COMPOSITES Admin Panel.`;

      // Send via Web3Forms (client-side)
      const w3res = await fetch('https://api.web3forms.com/submit', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          access_key: WEB3FORMS_KEY,
          subject: `Weekly Analytics Report - ${period.label || 'Report'}`,
          from_name: 'TOTAL COMPOSITES Admin',
          message: message,
          botcheck: '',
        }),
      });
      const w3data = await w3res.json();

      if (w3data.success) {
        // Mark report as sent in backend
        await fetch(`${API}/api/portal/admin/reports/${report.id}/mark-sent`, {
          method: 'POST',
          headers: { ...authHeaders, 'Content-Type': 'application/json' },
        });
        showFlash('Digest email sent to info@totalcomposites.com');
        fetchReports();
      } else {
        showFlash(w3data.message || 'Failed to send email', true);
      }
    } catch (e) {
      showFlash('Error sending digest email', true);
    }
    setSending(false);
  };

  const Arrow = ({ val }) => {
    if (val >= 0) return <span className="inline-flex items-center gap-0.5 text-green-600 font-bold"><TrendingUp size={11} />+{val}%</span>;
    return <span className="inline-flex items-center gap-0.5 text-red-600 font-bold"><TrendingDown size={11} />{val}%</span>;
  };

  return (
    <div data-testid="weekly-reports-section">
      {/* Flash message */}
      {flash && (
        <div className={`mb-4 px-4 py-3 rounded-xl text-sm font-bold flex items-center gap-2 ${
          flash.isError ? 'bg-red-50 text-red-700 border border-red-200' : 'bg-green-50 text-green-700 border border-green-200'
        }`}>
          {flash.isError ? <AlertCircle size={16} /> : <CheckCircle size={16} />}
          {flash.msg}
        </div>
      )}

      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 mb-5">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-lg flex items-center justify-center" style={{ backgroundColor: '#FFF0E8' }}>
            <BarChart3 size={16} style={{ color: '#E8520A' }} />
          </div>
          <div>
            <h3 className="text-sm sm:text-base font-black text-gray-900">Weekly Reports</h3>
            <p className="text-[10px] text-gray-400">{reports.length} report{reports.length !== 1 ? 's' : ''} generated</p>
          </div>
        </div>

        <div className="flex flex-wrap items-center gap-2">
          {/* Report selector */}
          <div className="relative" data-testid="report-selector">
            <button
              onClick={() => setShowSelector(!showSelector)}
              className="flex items-center gap-2 px-3 py-2 bg-white border border-gray-200 rounded-lg text-xs font-bold text-gray-700 hover:border-gray-300 transition-all"
            >
              <Calendar size={12} style={{ color: '#E8520A' }} />
              <span className="max-w-[180px] truncate">
                {selectedReport ? selectedReport.period?.label : 'Select report'}
              </span>
              <ChevronDown size={12} className={`text-gray-400 transition-transform ${showSelector ? 'rotate-180' : ''}`} />
            </button>
            {showSelector && (
              <div className="absolute right-0 top-full mt-1 bg-white rounded-xl shadow-xl border border-gray-200 w-72 max-h-64 overflow-y-auto z-50" data-testid="report-selector-dropdown">
                {reports.length === 0 ? (
                  <div className="px-4 py-6 text-center text-xs text-gray-400">
                    No reports yet. Generate your first report.
                  </div>
                ) : (
                  reports.map(r => (
                    <button
                      key={r.id}
                      onClick={() => { setSelectedReport(r); setShowSelector(false); }}
                      className={`w-full flex items-center gap-3 px-3 py-2.5 text-left transition-colors border-b border-gray-50 last:border-0 ${
                        selectedReport?.id === r.id ? 'bg-orange-50' : 'hover:bg-gray-50'
                      }`}
                      data-testid={`report-option-${r.id}`}
                    >
                      <FileText size={14} className={selectedReport?.id === r.id ? 'text-orange-600' : 'text-gray-400'} />
                      <div className="flex-1 min-w-0">
                        <p className={`text-xs font-bold truncate ${selectedReport?.id === r.id ? 'text-orange-700' : 'text-gray-800'}`}>
                          {r.period?.label}
                        </p>
                        <p className="text-[10px] text-gray-400">
                          Generated {new Date(r.generated_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric', hour: '2-digit', minute: '2-digit' })}
                          {r.email_sent && <span className="ml-1 text-green-600">· Emailed</span>}
                        </p>
                      </div>
                      {selectedReport?.id === r.id && <CheckCircle size={12} className="text-orange-500 flex-shrink-0" />}
                    </button>
                  ))
                )}
              </div>
            )}
          </div>

          {/* Generate button */}
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
            style={{ backgroundColor: '#E8520A' }}
            data-testid="generate-report-btn"
          >
            {generating ? <Loader size={12} className="animate-spin" /> : <FileText size={12} />}
            {generating ? 'Generating...' : 'Generate Report'}
          </button>

          {/* Send digest button */}
          {selectedReport && (
            <button
              onClick={() => handleSendDigest(selectedReport.id)}
              disabled={sending}
              className="flex items-center gap-1.5 px-3 py-2 text-xs font-bold rounded-lg transition-all hover:opacity-90 disabled:opacity-50"
              style={{ backgroundColor: '#1a1a1a', color: '#fff' }}
              data-testid="send-digest-btn"
            >
              {sending ? <Loader size={12} className="animate-spin" /> : <Send size={12} />}
              {sending ? 'Sending...' : 'Send Digest Email'}
            </button>
          )}
        </div>
      </div>

      {/* Report Content */}
      {loading ? (
        <div className="text-center py-12">
          <Loader size={24} className="mx-auto text-gray-300 animate-spin mb-2" />
          <p className="text-xs text-gray-400">Loading reports...</p>
        </div>
      ) : selectedReport ? (
        <div className="space-y-4" data-testid="report-content">
          {/* Report header info */}
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-2 p-4 bg-gray-50 rounded-xl border border-gray-100">
            <div>
              <h4 className="text-sm font-black text-gray-900">{selectedReport.period?.label}</h4>
              <p className="text-[10px] text-gray-400 mt-0.5">
                Generated by {selectedReport.generated_by} on {new Date(selectedReport.generated_at).toLocaleDateString('en-CA', { month: 'long', day: 'numeric', year: 'numeric', hour: '2-digit', minute: '2-digit' })}
              </p>
            </div>
            <div className="flex items-center gap-2">
              {selectedReport.email_sent && (
                <span className="inline-flex items-center gap-1 px-2 py-1 bg-green-50 text-green-700 text-[10px] font-bold rounded-full border border-green-200">
                  <CheckCircle size={10} /> Emailed {selectedReport.email_sent_at ? new Date(selectedReport.email_sent_at).toLocaleDateString('en-CA', { month: 'short', day: 'numeric' }) : ''}
                </span>
              )}
              <span className="inline-flex items-center gap-1 px-2 py-1 bg-gray-100 text-gray-600 text-[10px] font-bold rounded-full">
                <Clock size={10} /> Report #{reports.findIndex(r => r.id === selectedReport.id) + 1}
              </span>
            </div>
          </div>

          {/* Performance stats */}
          <div className="grid grid-cols-2 sm:grid-cols-5 gap-3" data-testid="report-stats">
            {[
              { key: 'visits', label: 'Visits', icon: Eye, color: '#E8520A' },
              { key: 'orders', label: 'Orders', icon: Package, color: '#2563eb' },
              { key: 'chats', label: 'Chats', icon: MessageSquare, color: '#7c3aed' },
              { key: 'forms', label: 'Forms', icon: FileText, color: '#16a34a' },
              { key: 'total_leads', label: 'Total Leads', icon: TrendingUp, color: '#d97706' },
            ].map(({ key, label, icon: Icon, color }) => (
              <div key={key} className="bg-white border border-gray-100 rounded-xl p-3 hover:shadow-sm transition-shadow" data-testid={`report-stat-${key}`}>
                <div className="flex items-center justify-between mb-2">
                  <span className="text-[10px] font-bold uppercase tracking-wider text-gray-400">{label}</span>
                  <Icon size={14} style={{ color }} />
                </div>
                <p className="text-lg sm:text-xl font-black text-gray-900">{selectedReport.stats?.[key] ?? 0}</p>
                {key !== 'total_leads' && selectedReport.changes?.[key] !== undefined && (
                  <div className="mt-1 text-[10px]">
                    <Arrow val={selectedReport.changes[key]} />
                    <span className="text-gray-400 ml-1">vs prior</span>
                  </div>
                )}
              </div>
            ))}
          </div>

          {/* Two-column: Top Countries + Top Pages */}
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            {/* Top Countries */}
            <div className="bg-white border border-gray-100 rounded-xl p-4" data-testid="report-countries">
              <h5 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                <Globe size={12} style={{ color: '#E8520A' }} /> Top Countries
              </h5>
              {(selectedReport.top_countries?.filter(c => c.country)?.length > 0) ? (
                <div className="space-y-2">
                  {selectedReport.top_countries.filter(c => c.country).map((c, i) => (
                    <div key={c.country} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-300 w-4 text-right">{i + 1}</span>
                      <span className="text-xs text-gray-700 flex-1">{c.country}</span>
                      <span className="text-xs font-bold text-gray-900">{c.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">No geo data for this period</p>
              )}
            </div>

            {/* Top Pages */}
            <div className="bg-white border border-gray-100 rounded-xl p-4" data-testid="report-pages">
              <h5 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                <ExternalLink size={12} style={{ color: '#E8520A' }} /> Top Pages
              </h5>
              {(selectedReport.top_pages?.length > 0) ? (
                <div className="space-y-2">
                  {selectedReport.top_pages.map((p, i) => (
                    <div key={p.path || p.page} className="flex items-center gap-3">
                      <span className="text-xs font-bold text-gray-300 w-4 text-right">{i + 1}</span>
                      <span className="text-xs text-gray-700 flex-1 truncate font-mono">{p.page}</span>
                      <span className="text-xs font-bold text-gray-900">{p.count}</span>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="text-xs text-gray-400 text-center py-4">No page data for this period</p>
              )}
            </div>
          </div>

          {/* Top Cities */}
          {selectedReport.top_cities?.length > 0 && (
            <div className="bg-white border border-gray-100 rounded-xl p-4" data-testid="report-cities">
              <h5 className="text-xs font-black uppercase tracking-widest text-gray-400 mb-3 flex items-center gap-2">
                <MapPin size={12} style={{ color: '#E8520A' }} /> Top Cities
              </h5>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-6 gap-y-2">
                {selectedReport.top_cities.map((c, i) => (
                  <div key={c.city || c.name} className="flex items-center gap-3">
                    <span className="text-xs font-bold text-gray-300 w-4 text-right">{i + 1}</span>
                    <span className="text-xs text-gray-700 flex-1">{c.city}, {c.country}</span>
                    <span className="text-xs font-bold text-gray-900">{c.count}</span>
                  </div>
                ))}
              </div>
            </div>
          )}
        </div>
      ) : (
        /* Empty state */
        <div className="text-center py-12 bg-white border border-gray-100 rounded-xl">
          <BarChart3 size={36} className="mx-auto text-gray-300 mb-3" />
          <p className="text-sm font-bold text-gray-500">No reports generated yet</p>
          <p className="text-xs text-gray-400 mt-1 mb-4">Generate your first weekly analytics report</p>
          <button
            onClick={handleGenerate}
            disabled={generating}
            className="inline-flex items-center gap-1.5 px-4 py-2.5 text-xs font-bold text-white rounded-lg transition-all hover:opacity-90"
            style={{ backgroundColor: '#E8520A' }}
          >
            <FileText size={12} /> Generate First Report
          </button>
        </div>
      )}
    </div>
  );
};

export default WeeklyReportsSection;
